/**
 * Icon paths for `@icon(...)` annotations.
 *
 * Plain path data on a 24x24 grid, stroked with `currentColor`. Kept as data
 * rather than components so the headless SVG exporter can draw the same icons
 * without React. The set is closed — `resolveMetadata` rejects any slug that is
 * not a key here, so a document can never reference an icon that does not exist.
 */
import type { IconSlug } from '../language/parser/metadata';

/** One or more subpaths, all stroked (never filled). */
export const NODE_ICON_PATHS: Record<IconSlug, readonly string[]> = {
  user: ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z', 'M4 21a8 8 0 0 1 16 0'],
  database: [
    'M12 8c4.4 0 8-1.34 8-3s-3.6-3-8-3-8 1.34-8 3 3.6 3 8 3Z',
    'M20 5v14c0 1.66-3.6 3-8 3s-8-1.34-8-3V5',
    'M20 12c0 1.66-3.6 3-8 3s-8-1.34-8-3',
  ],
  mail: ['M3 5h18v14H3z', 'm3 6 9 7 9-7'],
  lock: ['M5 11h14v10H5z', 'M8 11V7a4 4 0 1 1 8 0v4'],
  clock: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M12 7v5l3 2'],
  alert: ['M12 3 2 20h20L12 3Z', 'M12 9v5', 'M12 17.5v.5'],
  check: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'm8 12 3 3 5-6'],
  card: ['M3 6h18v12H3z', 'M3 10h18', 'M7 15h4'],
  file: ['M6 3h8l4 4v14H6z', 'M14 3v4h4'],
  cloud: ['M7 19a4 4 0 0 1 .5-8 5.5 5.5 0 0 1 10.6 1.4A3.5 3.5 0 0 1 17.5 19H7Z'],
  search: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z', 'm16 16 4.5 4.5'],
  settings: [
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
    'M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 9 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 9a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z',
  ],
  code: ['m8 7-5 5 5 5', 'm16 7 5 5-5 5', 'm14 4-4 16'],
  chart: ['M4 20V10', 'M10 20V4', 'M16 20v-7', 'M22 20H2'],
  cart: [
    'M8 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z',
    'M18 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z',
    'M2 3h3l2.6 12h11L21 7H6',
  ],
  key: [
    'M14.5 10.5a4.5 4.5 0 1 0-4.2 4.5L9 16.3 7.6 15l-1.4 1.4 1.4 1.4-1.7 1.7L3 18v-2.3l6.3-6.3',
    'M15.5 8.5v.01',
  ],
  shield: ['M12 3 4 6v6c0 5 3.4 8.3 8 9 4.6-.7 8-4 8-9V6l-8-3Z'],
  bell: ['M18 9a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7', 'M13.7 20a2 2 0 0 1-3.4 0'],
  tag: ['M3 3h8l10 10-8 8L3 11V3Z', 'M7.5 7.5v.01'],
  star: ['m12 3 2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.8 6.2 20.9l1.1-6.5L2.6 9.8l6.5-.9L12 3Z'],
};

export const NODE_ICON_VIEWBOX = 24;

export function iconPaths(slug: string | undefined): readonly string[] {
  if (!slug) return [];
  return NODE_ICON_PATHS[slug as IconSlug] ?? [];
}
